import AbstractProvidedClass from "../shared/AbstractProvidedClass";
import { DEFAULT_FILL_COLOR } from "../shared/constants";
import EventTypes from "../shared/EventTypes";
import { addSvg, createRowButton } from "../shared/utils";
import { REMOVE_SPECIES_FROM_ELEMENT } from "../tools/actions/ActionHelper";

export default class SpeciesRow extends AbstractProvidedClass {
    buttons;

    constructor(provider, species, rowContainer) {
        super(provider);

        this.species = species;

        this.rowDiv = this.p.createDiv()
            .parent(rowContainer)
            .id('species-row-' + this.species.id)
            .mouseOver(() => this.mouseOver())
            .mouseOut(() => this.mouseOut())
            .mouseClicked(() => this.click());
        rowContainer.elt.appendChild(this.rowDiv.elt);
        this.row = this.p.createDiv()
            .parent(this.rowDiv)
            .class('input-search-result');

        this.img = this.p.createImg(this.species.thumb || '/permapeople-permaculture-plant-database-blank.jpg')
            .parent(this.row)
            .class('data-img-xs');

        this.label = this.p.createElement('label', this.species.name + '<span>' + this.species.scientific_name + '</span>')
            .class('input-search-result-name')
            .parent(this.row);

        this.createOptions();

        if (this.touch) {
            if (this.viewMode) {
                // In view mode only the info button is relevant — show it directly
                Object.keys(this.buttons).forEach(key => {
                    if (key !== 'info') this.buttons[key].hide();
                });
                this.label.hide();
                this.options.show();
            } else {
                this.moreButton = this.p.createDiv()
                    .parent(this.row)
                    .class('btn-icon')
                    .style('margin-left', 'auto')
                    .mouseClicked(() => {
                        this.clickButton();
                        this.toggleOptions();
                    });
                addSvg(this.moreButton, 'more-2-fill');

                this.on(EventTypes.CLOSE_SIDEBAR, () => this.closeOptions());
            }
        }
    }

    mouseOver() {
        if (this.touch) return;
        this.speciesInfo.setSpecies(this.species);
        this.label.hide();
        this.options.show();
        if (this.buttons['addToSelection']) {
            this.selection.selected
                ? this.buttons['addToSelection'].style('display', 'flex')
                : this.buttons['addToSelection'].hide();
        }
    }

    mouseOut() {
        if (this.touch) return;
        this.speciesInfo.setSpecies(null);
        this.label.show();
        this.options.hide();
    }

    openOptions() {
        this.label.hide();
        this.moreButton?.hide();
        this.options.show();
        this._closeHandler = (e) => {
            if (!this.rowDiv.elt.contains(e.target)) {
                this.closeOptions();
            }
        };
        setTimeout(() => document.addEventListener('touchstart', this._closeHandler), 0);
    }

    closeOptions() {
        if (this._closeHandler) {
            document.removeEventListener('touchstart', this._closeHandler);
            this._closeHandler = null;
        }
        this.label.show();
        this.moreButton?.show();
        this.options.hide();
    }

    toggleOptions() {
        if (this.options.elt.style.display === 'none') {
            this.openOptions();
        } else {
            this.closeOptions();
        }
    }

    without(exclude) {
        exclude.map((button) => {
            this.buttons[button].remove();
            delete this.buttons[button];
        });
        return this;
    }

    only(include) {
        Object.keys(this.buttons).map((button) => {
            if (!include.includes(button)) {
                this.buttons[button].remove();
                delete this.buttons[button];
            }
        });
        return this;
    }

    click() {
        if (this.editMode && !this.buttonClicked) {
            this.place();
        }
    }

    place() {
        this.clickButton();
        this.speciesCollection.add(this.species);
        this.tools.setTool('placement', [this.toPlant()]);
        if (this.mobile) {
            this.emit(EventTypes.CLOSE_SIDEBAR);
        }
    }

    drawShape() {
        this.clickButton();
        this.speciesCollection.add(this.species);
        this.tools.setTool('shapes', this.species.id);
        if (this.mobile) {
            const html = `<svg class="icon"><use xlink:href="/icons/__remixicon.sprite.svg?v=491#shape-line"></use></svg><img src="${this.species.thumb || '/permapeople-permaculture-plant-database-blank.jpg'}" style="position:absolute;bottom:-6px;right:-3px;width:14px;height:14px;border-radius:50%;border:1px solid #fff;">`;
            this.provider.menu.setCurrentToolIcon(html);
            this.emit(EventTypes.CLOSE_SIDEBAR);
        } else {
            this.sideBar.setTab('info');
        }
    }

    addToElement() {
        this.clickButton();
        this.speciesCollection.add(this.species);
        this.tools.setTool('add_species', this.species);
        if (this.mobile) {
            this.emit(EventTypes.CLOSE_SIDEBAR);
        }
    }

    addToSelection() {
        this.clickButton();
        this.speciesCollection.add(this.species);
        this.selection.addSpecies(this.species);
        this.getTab('info').setCurrentElement(this.selection.first);
    }

    removeSpecies() {
        this.clickButton();
        this.actions.execute(REMOVE_SPECIES_FROM_ELEMENT, this.selection.first, this.species);
        this.getTab('info').setCurrentElement(this.selection.first);
        this.speciesInfo.setSpecies(null);
    }

    clickButton() {
        this.buttonClicked = true;
        setTimeout(() => this.buttonClicked = false, 100);
    }

    createOptions() {
        this.options = this.p.createDiv()
            .parent(this.row);
        this.optionsContainer = this.p.createDiv()
            .parent(this.options)
            .class('row');

        this.options.hide();

        let createButton = (toolTip, func, svgId) => {
            return createRowButton(this.p, this.optionsContainer, toolTip, func, svgId);
        };

        this.buttons = {
            'plant': createButton('Plant this', () => this.place(), 'plant-line'),
            'draw': createButton('Draw into shape', () => this.drawShape(), 'shape-line'),
            'add': createButton('Add to element', () => this.addToElement(), 'add-line'),
            'addToSelection': createButton('Add to selection', () => this.addToSelection(), 'add-box-line'),
            'remove': createButton('Remove', () => this.removeSpecies(), 'close-circle-line'),
            'info': this.createInfoButton(),
        };
    }

    createInfoButton() {
        this.infoButton = this.p.createA(this.species['link'], '', '_blank')
            .parent(this.optionsContainer)
            .class('btn-icon')
            .attribute('data-tooltip', 'Profile')
        addSvg(this.infoButton, 'information-line');
        return this.infoButton;
    }

    toPlant() {
        const size = this.species.Width && this.species.Width != 'not specified' ? parseFloat(this.species.Width) : 1
        const layer = Array.isArray(this.species.Layer) ? this.species.Layer[0] : this.species.Layer;
        return this.elementFactory.makeEllipse({
            type: 'ellipse',
            data: {
                x: 0,
                y: 0,
                width: size * 100,
                height: size * 100,
                labels: [
                    {
                        type: 'species',
                        id: this.species.id,
                        keys: this.species.type !== 'Variety'
                            ? ['name', 'scientific_name']
                            : ['parent_name', 'name', 'scientific_name']
                    }
                ],
                labelX: 0,
                labelY: 0,
                fillColor: DEFAULT_FILL_COLOR,
                strokeColor: null,
                strokeWidth: null,
                layer: layer,
                canopyStyle: 'lobed',
                canopySeed: Math.floor(Math.random() * 100000),
            },
            speciesIds: [this.species.id],
        });
    }

    delete() {
        this.rowDiv.remove();
    }

    show() {
        this.rowDiv.show();
    }

    hide() {
        this.rowDiv.hide();
    }
}
